import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, FolderKanban, Trash2, Flag, Check, Building2, TrendingUp, Sparkles, Target, User } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import LifecycleDraftModal from "@/app/components/LifecycleDraftModal";

const inputCls = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";
const STATUS_LABELS = { planning: "Planning", active: "Active", on_hold: "On hold", completed: "Completed" };

export default function ProjectsEngine() {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [deals, setDeals] = useState([]);
  const [members, setMembers] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", deadline: "", company_id: "", deal_id: "" });
  const [msForm, setMsForm] = useState({});
  const [aiPreview, setAiPreview] = useState(null); // { project_id, preview }
  const [aiBusy, setAiBusy] = useState(false);
  const [lifecycleProject, setLifecycleProject] = useState(null); // {id, name}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c, d, m, g] = await Promise.all([
        api.get("/projects"),
        api.get("/companies"),
        api.get("/deals"),
        api.get("/organizations/me/members").catch(() => ({ data: { members: [] } })),
        api.get("/goals").catch(() => ({ data: { goals: [] } })),
      ]);
      setProjects(p.data.projects || []);
      setCompanies(c.data.companies || []);
      setDeals(d.data.deals || []);
      setMembers(m.data.members || []);
      setGoals(g.data.goals || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load projects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addProject = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      const payload = { name: form.name.trim(), status: "planning" };
      if (form.deadline) payload.deadline = form.deadline;
      if (form.company_id) payload.company_id = form.company_id;
      if (form.deal_id) payload.deal_id = form.deal_id;
      const { data } = await api.post("/projects", payload);
      setProjects((p) => [{ ...data.project, milestones: [] }, ...p]);
      setForm({ name: "", deadline: "", company_id: "", deal_id: "" });
      toast.success("Project created");
      setLifecycleProject({ id: data.project.id, name: data.project.name });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // When a deal is picked in the add-form, auto-inherit its company for the UI preview.
  const onFormDealChange = (deal_id) => {
    const d = deals.find((x) => x.id === deal_id);
    setForm((f) => ({
      ...f,
      deal_id,
      company_id: f.company_id || (d?.company_id || ""),
    }));
  };

  const updateProject = async (id, patch) => {
    try {
      const { data } = await api.patch(`/projects/${id}`, patch);
      setProjects((all) => all.map((p) => (p.id === id ? { ...data.project, milestones: p.milestones } : p)));
    } catch { toast.error("Could not update project."); }
  };

  // When updating a project's deal, auto-inherit company client-side too.
  const updateProjectDeal = (id, deal_id) => {
    const d = deals.find((x) => x.id === deal_id);
    const patch = { deal_id: deal_id || null };
    if (deal_id && d?.company_id) patch.company_id = d.company_id;
    updateProject(id, patch);
  };

  const removeProject = async (id) => {
    if (!window.confirm("Delete this project and its milestones?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((all) => all.filter((p) => p.id !== id));
    } catch { toast.error("Could not delete."); }
  };

  const addMilestone = async (pid) => {
    const val = (msForm[pid] || "").trim();
    if (!val) return;
    try {
      const { data } = await api.post(`/projects/${pid}/milestones`, { name: val });
      setProjects((all) => all.map((p) => (p.id === pid ? { ...p, milestones: [...(p.milestones || []), data.milestone] } : p)));
      setMsForm({ ...msForm, [pid]: "" });
    } catch { toast.error("Could not add milestone."); }
  };

  const toggleMilestone = async (pid, m) => {
    try {
      await api.patch(`/milestones/${m.id}`, { done: !m.done });
      // Refresh project to get updated auto-progress
      const { data } = await api.get(`/projects`);
      setProjects(data.projects || []);
    } catch { toast.error("Could not update milestone."); }
  };

  const generateAIPlan = async (pid) => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/projects/${pid}/ai-plan`);
      setAiPreview({ project_id: pid, preview: data.preview });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate plan.");
    } finally { setAiBusy(false); }
  };

  const approveAIPlan = async () => {
    if (!aiPreview) return;
    try {
      const milestones = (aiPreview.preview?.milestones || []).filter((m) => m.name);
      const { data } = await api.post(`/projects/${aiPreview.project_id}/approve-plan`, { milestones });
      toast.success(`Added ${data.created_milestones} milestones · ${data.created_tasks} tasks`);
      setAiPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not approve plan.");
    }
  };

  return (
    <div data-testid="projects-engine">
      <Seo title="Projects" description="Projects, milestones and progress." path="/app/projects" />
      <header className="mb-6">
        <p className="eyebrow text-brand-teal">Projects Engine</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Everything you're building.</h1>
        <p className="mt-2 text-sm text-muted-foreground">{projects.length} project{projects.length === 1 ? "" : "s"}</p>
      </header>

      <form onSubmit={addProject} className="mb-6 grid gap-2 rounded-2xl border border-border bg-card p-3 md:grid-cols-2" data-testid="project-form">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="New project name…" className={`${inputCls} md:col-span-2`} data-testid="project-name-input" />
        <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className={inputCls} data-testid="project-deadline-input" />
        <select
          value={form.deal_id}
          onChange={(e) => onFormDealChange(e.target.value)}
          className={inputCls}
          data-testid="project-deal-select"
        >
          <option value="">Link a deal (optional)…</option>
          {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
        <select
          value={form.company_id}
          onChange={(e) => setForm({ ...form, company_id: e.target.value })}
          className={inputCls}
          data-testid="project-company-select"
        >
          <option value="">Link a company (optional)…</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button type="submit" data-testid="project-add-btn" className="inline-flex items-center justify-center gap-1.5 rounded-xl brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add</button>
      </form>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">No projects yet.</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="project-list">
          {projects.map((p) => {
            const co = companies.find((c) => c.id === p.company_id);
            const dl = deals.find((d) => d.id === p.deal_id);
            return (
            <div key={p.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-brand-teal"><FolderKanban className="h-4 w-4" /></span>
                    <h3 className="truncate font-display text-lg font-semibold">{p.name}</h3>
                    {p.source_board_decision_id && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary" data-testid={`from-board-badge-project-${p.id}`} title="Created from a Board decision">
                        <Sparkles className="h-2.5 w-2.5" /> From Board
                      </span>
                    )}
                  </div>
                  {(co || dl || p.deadline) && (
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      {p.deadline && <span className="inline-flex items-center gap-1"><Flag className="h-3 w-3" /> Deadline {p.deadline.slice(0, 10)}</span>}
                      {co && <span data-testid={`project-company-chip-${p.id}`} className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {co.name}</span>}
                      {dl && <span data-testid={`project-deal-chip-${p.id}`} className="inline-flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {dl.title}</span>}
                    </div>
                  )}
                </div>
                <button onClick={() => removeProject(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`project-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <select value={p.status} onChange={(e) => updateProject(p.id, { status: e.target.value })} data-testid={`project-status-${p.id}`} className="rounded-lg border border-border bg-background px-2 py-1 text-xs">
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <div className="flex-1">
                  <div className="mb-1 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Progress</span><span>{p.progress || 0}%</span>
                  </div>
                  <input type="range" min={0} max={100} step={5} value={p.progress || 0} onChange={(e) => updateProject(p.id, { progress: Number(e.target.value) })} data-testid={`project-progress-${p.id}`} className="w-full" />
                </div>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <label className="block text-[11px] font-medium text-muted-foreground">
                  Related deal
                  <select
                    value={p.deal_id || ""}
                    onChange={(e) => updateProjectDeal(p.id, e.target.value)}
                    className={`${inputCls} mt-1`}
                    data-testid={`project-deal-select-${p.id}`}
                  >
                    <option value="">—</option>
                    {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                  </select>
                </label>
                <label className="block text-[11px] font-medium text-muted-foreground">
                  Related company
                  <select
                    value={p.company_id || ""}
                    onChange={(e) => updateProject(p.id, { company_id: e.target.value || null })}
                    className={`${inputCls} mt-1`}
                    data-testid={`project-company-select-${p.id}`}
                  >
                    <option value="">—</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label className="block text-[11px] font-medium text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> Owner</span>
                  <select
                    value={p.owner_id || ""}
                    onChange={(e) => updateProject(p.id, { owner_id: e.target.value || null })}
                    className={`${inputCls} mt-1`}
                    data-testid={`project-owner-select-${p.id}`}
                  >
                    <option value="">Unassigned</option>
                    {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                  </select>
                </label>
                <label className="block text-[11px] font-medium text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" /> Related goal</span>
                  <select
                    value={p.related_goal_id || ""}
                    onChange={(e) => updateProject(p.id, { related_goal_id: e.target.value || null })}
                    className={`${inputCls} mt-1`}
                    data-testid={`project-goal-select-${p.id}`}
                  >
                    <option value="">—</option>
                    {goals.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </label>
              </div>

              <div className="mt-4 border-t border-border pt-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Milestones</p>
                  <button
                    onClick={() => generateAIPlan(p.id)}
                    disabled={aiBusy}
                    data-testid={`project-ai-plan-${p.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-brand-teal/40 bg-brand-teal/10 px-2.5 py-1 text-[11px] font-semibold text-brand-teal hover:bg-brand-teal/20 disabled:opacity-60"
                  >
                    <Sparkles className="h-3 w-3" /> {aiBusy ? "Thinking…" : "AI Plan"}
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(p.milestones || []).map((m) => (
                    <button key={m.id} onClick={() => toggleMilestone(p.id, m)} data-testid={`milestone-${m.id}`} className="flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left text-sm hover:bg-muted">
                      <span className={`grid h-4 w-4 place-items-center rounded border ${m.done ? "border-brand-teal bg-brand-teal text-white" : "border-border"}`}>{m.done && <Check className="h-3 w-3" />}</span>
                      <span className={m.done ? "text-muted-foreground line-through" : ""}>{m.name}</span>
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <input value={msForm[p.id] || ""} onChange={(e) => setMsForm({ ...msForm, [p.id]: e.target.value })} placeholder="Add a milestone…" className={inputCls + " flex-1"} data-testid={`milestone-input-${p.id}`} />
                  <button onClick={() => addMilestone(p.id)} data-testid={`milestone-add-${p.id}`} className="rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted">Add</button>
                </div>
              </div>
            </div>
          );
          })}
        </div>
      )}

      {aiPreview && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={() => setAiPreview(null)} data-testid="ai-plan-preview">
          <div onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-card p-6 sm:rounded-3xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-semibold tracking-tight">AI-drafted plan</h3>
                <p className="mt-1 text-xs text-muted-foreground">Approve to persist as milestones + tasks.</p>
              </div>
              <button onClick={() => setAiPreview(null)} className="text-muted-foreground hover:text-foreground" data-testid="ai-plan-close">
                ✕
              </button>
            </div>
            <div className="space-y-3">
              {(aiPreview.preview?.milestones || []).map((m, i) => (
                <div key={m.temp_id || i} className="rounded-xl border border-border bg-background p-3">
                  <p className="text-sm font-semibold">{m.name}</p>
                  {m.description && <p className="mt-0.5 text-xs text-muted-foreground">{m.description}</p>}
                  <ul className="mt-2 space-y-1">
                    {(m.tasks || []).map((t, j) => (
                      <li key={t.temp_id || j} className="flex items-start gap-2 text-xs">
                        <span className="mt-1 h-1 w-1 rounded-full bg-brand-teal" />
                        <span className="flex-1"><span className="font-medium">{t.title}</span> {t.priority && <span className="ml-1 rounded bg-muted px-1 text-[10px] uppercase text-muted-foreground">{t.priority}</span>}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {(!aiPreview.preview?.milestones || aiPreview.preview.milestones.length === 0) && (
                <p className="text-sm text-muted-foreground">The AI didn't return a structured plan. Try again with more project detail.</p>
              )}
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setAiPreview(null)} data-testid="ai-plan-cancel" className="flex-1 rounded-full border border-border px-4 py-2 text-sm font-semibold">Cancel</button>
              <button onClick={approveAIPlan} disabled={!(aiPreview.preview?.milestones || []).length} data-testid="ai-plan-approve" className="flex-1 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Approve & add</button>
            </div>
          </div>
        </div>
      )}
      <LifecycleDraftModal
        trigger="project_created"
        entityId={lifecycleProject?.id}
        entityLabel={lifecycleProject?.name}
        open={!!lifecycleProject}
        onClose={() => setLifecycleProject(null)}
        onApproved={() => { load(); setLifecycleProject(null); }}
      />
    </div>
  );
}
